// @ts-nocheck
import { useCallback, useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { AI_AGENT_FIELD_LIMITS } from '@/utilities/constant';
import { getAiAgentDescriptionCharLimit } from '@/utilities/aiAgentAcl';
import { Button } from '@/components/ui/button';
import {
  Building2,
  Check,
  ChevronLeft,
  ChevronRight,
  Headset,
  Plus,
  Scale,
  Trash2,
  TrendingUp,
} from 'lucide-react';

import {
  Briefcase,
  Car,
  ConciergeBell,
  GraduationCap,
  Landmark,
  Plane,
  ShoppingBag,
  Stethoscope,
  Truck,
  UtensilsCrossed,
} from './agentIcons';
import { cn } from '@/lib/utils/cn';
import CollapsibleSection from './CollapsibleSection';
import { fieldTone } from '@/lib/utils/fieldTone';
import HeadingInfo from './HeadingInfo';
import { getPersonaTypes } from '@/service/aiAgentApi';

const { PERSONA_GREETING_MAX, NEVER_SAY_MAX_ITEMS, NEVER_SAY_MAX } = AI_AGENT_FIELD_LIMITS;


const PERSONA_ICON_RULES: { keywords: string[]; icon: any; accent: string; tint: string; tagline: string }[] = [
  {
    keywords: ['real estate', 'realestate', 'real_estate', 'property', 'realty', 'housing'],
    icon: Building2,
    accent: '#2563eb',
    tint: 'bg-blue-50 text-blue-600 dark:bg-blue-950/40 dark:text-blue-300',
    tagline: 'Listings, site visits & leads',
  },
  {
    keywords: ['hospitality', 'hotel', 'resort', 'lodging', 'front desk'],
    icon: ConciergeBell,
    accent: '#d97706',
    tint: 'bg-amber-50 text-amber-700 dark:bg-amber-950/40 dark:text-amber-300',
    tagline: 'Bookings, stays & guests',
  },
  {
    keywords: ['restaurant', 'food', 'dining', 'cafe', 'catering'],
    icon: UtensilsCrossed,
    accent: '#ea580c',
    tint: 'bg-orange-50 text-orange-700 dark:bg-orange-950/40 dark:text-orange-300',
    tagline: 'Orders, tables & menus',
  },
  {
    keywords: ['health', 'clinic', 'hospital', 'medical', 'pharma'],
    icon: Stethoscope,
    accent: '#e11d48',
    tint: 'bg-rose-50 text-rose-700 dark:bg-rose-950/40 dark:text-rose-300',
    tagline: 'Appointments & patient care',
  },
  {
    keywords: ['education', 'school', 'college', 'university', 'edtech', 'coaching'],
    icon: GraduationCap,
    accent: '#7c3aed',
    tint: 'bg-violet-50 text-violet-700 dark:bg-violet-950/40 dark:text-violet-300',
    tagline: 'Admissions & student queries',
  },
  {
    keywords: ['bank', 'finance', 'insurance', 'loan', 'fintech'],
    icon: Landmark,
    accent: '#059669',
    tint: 'bg-emerald-50 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-300',
    tagline: 'Accounts, loans & claims',
  },
  {
    keywords: ['retail', 'ecommerce', 'e-commerce', 'shop', 'store'],
    icon: ShoppingBag,
    accent: '#db2777',
    tint: 'bg-pink-50 text-pink-700 dark:bg-pink-950/40 dark:text-pink-300',
    tagline: 'Orders, returns & stock',
  },
  {
    keywords: ['travel', 'tour', 'airline', 'flight'],
    icon: Plane,
    accent: '#0284c7',
    tint: 'bg-sky-50 text-sky-700 dark:bg-sky-950/40 dark:text-sky-300',
    tagline: 'Itineraries & bookings',
  },
  {
    keywords: ['auto', 'car', 'vehicle', 'dealership'],
    icon: Car,
    accent: '#0891b2',
    tint: 'bg-cyan-50 text-cyan-700 dark:bg-cyan-950/40 dark:text-cyan-300',
    tagline: 'Test drives & service',
  },
  {
    keywords: ['logistics', 'delivery', 'transport', 'courier'],
    icon: Truck,
    accent: '#4f46e5',
    tint: 'bg-indigo-50 text-indigo-700 dark:bg-indigo-950/40 dark:text-indigo-300',
    tagline: 'Shipments & tracking',
  },
  {
    keywords: ['legal', 'law', 'advocate'],
    icon: Scale,
    accent: '#475569',
    tint: 'bg-slate-100 text-slate-700 dark:bg-slate-800/60 dark:text-slate-300',
    tagline: 'Consultations & case intake',
  },
  {
    keywords: ['sales', 'marketing', 'lead'],
    icon: TrendingUp,
    accent: '#16a34a',
    tint: 'bg-green-50 text-green-700 dark:bg-green-950/40 dark:text-green-300',
    tagline: 'Lead qualification & follow-up',
  },
  {
    keywords: ['support', 'helpdesk', 'help desk', 'customer service', 'call center', 'call centre'],
    icon: Headset,
    accent: '#0d9488',
    tint: 'bg-teal-50 text-teal-700 dark:bg-teal-950/40 dark:text-teal-300',
    tagline: 'Tickets & customer queries',
  },
];

const DEFAULT_PERSONA_STYLE = {
  icon: Briefcase,
  accent: '#4b5563',
  tint: 'bg-muted text-muted-foreground',
  tagline: 'General purpose voice agent',
};

function getPersonaStyle(persona: any) {
  const haystack = `${persona?.name || ''} ${persona?.type || ''}`.toLowerCase();
  const rule = PERSONA_ICON_RULES.find((r) => r.keywords.some((k) => haystack.includes(k)));
  return rule || DEFAULT_PERSONA_STYLE;
}

export default function PersonaTypeSection({
  value,
  onChange,
  personaData,
  onPersonaDataChange,
}: any) {
  const [personaTypes, setPersonaTypes] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const scrollerRef = useRef<HTMLDivElement>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  const updateScrollState = useCallback(() => {
    const el = scrollerRef.current;
    if (!el) return;
    setCanScrollLeft(el.scrollLeft > 4);
    setCanScrollRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 4);
  }, []);


  useEffect(() => {
    const el = scrollerRef.current;
    if (!el) return;
    updateScrollState();
    el.addEventListener('scroll', updateScrollState, { passive: true });
    const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(updateScrollState) : null;
    ro?.observe(el);
    return () => {
      el.removeEventListener('scroll', updateScrollState);
      ro?.disconnect();
    };
  }, [loading, personaTypes, updateScrollState]);


  useEffect(() => {
    const el = scrollerRef.current;
    if (!el || loading) return;
    const tile = el.querySelector('[aria-checked="true"]') as HTMLElement | null;
    if (!tile) return;
    const tileEnd = tile.offsetLeft + tile.offsetWidth;
    if (tileEnd > el.clientWidth) el.scrollLeft = Math.max(0, tile.offsetLeft - 8);

  }, [loading, personaTypes]);

  const scrollTiles = (direction: -1 | 1) => {
    const el = scrollerRef.current;
    if (!el) return;
    el.scrollBy({ left: direction * el.clientWidth * 0.8, behavior: 'smooth' });
  };

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    getPersonaTypes()
      .then((list) => {
        if (cancelled) return;
        setPersonaTypes(Array.isArray(list) ? list : []);
        setError('');
      })
      .catch((err) => {
        if (cancelled) return;
        setError(typeof err === 'string' ? err : 'Failed to load persona types');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const patchData = (fields: any) => onPersonaDataChange({ ...personaData, ...fields });

 
  const requireOnBlur = (id: string, value: string | undefined, label: string) => {
    if (!value?.trim()) toast.error(`${label} is required`, { id });
  };

  const doNotSay: string[] = personaData?.do_not_say || [];

  const addDoNotSayRule = () => {
    if (doNotSay.length >= NEVER_SAY_MAX_ITEMS) {
      toast.error(`You can add up to ${NEVER_SAY_MAX_ITEMS} "Do Not Say" rules (max reached)`);
      return;
    }
    patchData({ do_not_say: [...doNotSay, ''] });
  };

  const updateDoNotSayRule = (idx: number, raw: string) => {
    let text = raw;
    if (text.length > NEVER_SAY_MAX) {
      toast.error(`Each "Do Not Say" rule can be max ${NEVER_SAY_MAX} characters`, {
        id: `never-say-max-${idx}`,
      });
      text = text.slice(0, NEVER_SAY_MAX);
    }
    patchData({ do_not_say: doNotSay.map((r, i) => (i === idx ? text : r)) });
  };

  const removeDoNotSayRule = (idx: number) => {
    patchData({ do_not_say: doNotSay.filter((_, i) => i !== idx) });
  };

  return (
    <CollapsibleSection title="Persona Type" required>
      <div className="space-y-4">
        <p className="text-sm font-semibold text-foreground">Persona Type</p>
        <p className="mt-1 text-xs text-muted-foreground">
          Define the personality and tone of your agent.
        </p>

        <div className="space-y-2">
          <Label className="flex items-center gap-1.5">
            Persona <span className="text-destructive">*</span>
            <HeadingInfo text="A preset that sets the agent's overall role." />
          </Label>

          {loading && (
            <div className="flex gap-3 overflow-hidden">
              {[0, 1, 2].map((i) => (
                <div key={i} className="h-32 w-40 shrink-0 animate-pulse rounded-xl border border-border bg-muted/50" />
              ))}
            </div>
          )}

          {!loading && !error && personaTypes.length === 0 && (
            <p className="rounded-md border border-dashed border-border p-3 text-xs italic text-muted-foreground">
              No persona types available.
            </p>
          )}

          {!loading && !error && personaTypes.length > 0 && (
            <>
              <div
                ref={scrollerRef}
                role="radiogroup"
                aria-label="Persona"
                className="relative flex snap-x snap-mandatory gap-3 overflow-x-auto scroll-smooth p-1 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
              >
                {personaTypes.map((p) => {
                  const style = getPersonaStyle(p);
                  const Icon = style.icon;
                  const selected = String(p.id) === String(value);
                  return (
                    <button
                      key={p.id}
                      type="button"
                      role="radio"
                      aria-checked={selected}
                      onClick={() => onChange(String(p.id))}
                      style={
                        selected
                          ? {
                              borderColor: style.accent,
                              boxShadow: `0 0 0 3px ${style.accent}1f, 0 10px 20px -12px ${style.accent}80`,
                            }
                          : undefined
                      }
                      className={cn(
                        'relative flex h-32 w-40 shrink-0 snap-start flex-col items-center justify-center gap-2.5 rounded-xl border-2 px-3 text-center transition-all focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/50',
                        selected
                          ? 'bg-card'
                          : 'border-border bg-card hover:-translate-y-0.5 hover:border-primary/40 hover:shadow-sm',
                      )}
                    >
                      {selected && (
                        <span
                          data-tile="check"
                          className="absolute right-2 top-2 flex h-5 w-5 items-center justify-center rounded-full text-white"
                          style={{ backgroundColor: style.accent }}
                        >
                          <Check className="h-3 w-3" />
                        </span>
                      )}
                      <span
                        data-tile="halo"
                        className={cn(
                          'flex h-11 w-11 items-center justify-center rounded-full transition-colors',
                          !selected && style.tint,
                        )}
                        style={selected ? { backgroundColor: style.accent, color: '#fff' } : undefined}
                      >
                        <Icon className="h-5 w-5" />
                      </span>
                      <span
                        data-tile="name"
                        className="line-clamp-1 max-w-full text-sm font-semibold leading-tight text-foreground"
                        style={selected ? { color: style.accent } : undefined}
                      >
                        {p.name}
                      </span>
                      <span className="line-clamp-2 text-[11px] leading-snug text-muted-foreground">
                        {style.tagline}
                      </span>
                    </button>
                  );
                })}
              </div>

              {(canScrollLeft || canScrollRight) && (
                <div className="flex items-center justify-between">
                  <Button
                    type="button"
                    variant="outline"
                    size="icon"
                    className="h-8 w-8"
                    onClick={() => scrollTiles(-1)}
                    disabled={!canScrollLeft}
                    aria-label="Previous personas"
                  >
                    <ChevronLeft className="h-4 w-4" />
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    size="icon"
                    className="h-8 w-8"
                    onClick={() => scrollTiles(1)}
                    disabled={!canScrollRight}
                    aria-label="Next personas"
                  >
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                </div>
              )}
            </>
          )}

          {error && <p className="text-xs text-destructive">{error}</p>}
        </div>

        {/* ---------------- Persona Data ---------------- */}
        <div className="space-y-4 border-t border-border pt-4">
          <p className="text-sm font-semibold text-foreground">Persona Data</p>

          {/* 1. Greeting */}
          <div className="space-y-1.5">
            <Label className="flex items-center gap-1.5">
              Greeting <span className="text-destructive">*</span>
              <HeadingInfo text="The first line the agent says when a call connects." />
            </Label>
            <Input
              className={fieldTone(personaData?.greeting?.trim() ? 'ok' : 'idle')}
              placeholder="e.g. Good day, thank you for calling. This is Smeeksha. How may I help you?"
              value={personaData?.greeting || ''}
              onChange={(e) => patchData({ greeting: e.target.value })}
              onBlur={(e) => requireOnBlur('persona-greeting', e.target.value, 'Greeting')}
              maxLength={PERSONA_GREETING_MAX}
            />
            <p className="text-right text-xs text-muted-foreground">
              {(personaData?.greeting || '').length}/{PERSONA_GREETING_MAX}
            </p>
          </div>

          {/* 2. Do Not Say — multiple rules, each editable + deletable */}
          <div className="space-y-1.5">
            <div className="flex items-center justify-between">
              <Label className="flex items-center gap-1.5">
                Do Not Say
                <HeadingInfo text="Topics or phrases the agent must never mention on a call. Example: refund amounts." />
                <span className="text-xs font-normal text-muted-foreground">
                  ({doNotSay.length}/{NEVER_SAY_MAX_ITEMS})
                </span>
              </Label>
              <Button
                size="sm"
                variant="outline"
                onClick={addDoNotSayRule}
                disabled={doNotSay.length >= NEVER_SAY_MAX_ITEMS}
              >
                <Plus className="h-4 w-4" />
                Add Rule
              </Button>
            </div>
            <p className={`text-xs ${doNotSay.length >= NEVER_SAY_MAX_ITEMS ? 'text-destructive' : 'text-muted-foreground'}`}>
              Things the agent must never do or say. Up to {NEVER_SAY_MAX_ITEMS} rules, {NEVER_SAY_MAX} characters each.
              {doNotSay.length >= NEVER_SAY_MAX_ITEMS ? ' Maximum rules reached.' : ''}
            </p>

            {doNotSay.length === 0 && (
              <p className="rounded-md border border-dashed border-border p-3 text-xs italic text-muted-foreground">
                No rules added yet. Click "Add Rule" to add one.
              </p>
            )}

            {doNotSay.map((rule, idx) => (
              <div key={idx} className="flex items-start gap-2 rounded-md border border-border p-2">
                <div className="flex-1">
                  <Textarea
                    rows={2}
                    placeholder="e.g. Never quote a rate, a room, or an availability that a tool has not returned."
                    value={rule}
                    onChange={(e) => updateDoNotSayRule(idx, e.target.value)}
                    className={rule.length >= NEVER_SAY_MAX ? 'border-destructive focus-visible:ring-destructive' : ''}
                  />
                  <div className="flex items-start justify-between gap-2">
                    <p className="text-xs text-destructive">
                      {rule.length >= NEVER_SAY_MAX ? `Max ${NEVER_SAY_MAX} characters reached` : ''}
                    </p>
                    <span className="shrink-0 text-xs text-muted-foreground">{rule.length}/{NEVER_SAY_MAX}</span>
                  </div>
                </div>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-7 w-7 shrink-0 text-destructive hover:text-destructive"
                  onClick={() => removeDoNotSayRule(idx)}
                  aria-label={`Remove rule ${idx + 1}`}
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </Button>
              </div>
            ))}
          </div>

          {/* 3. Display Name — plain input */}
          <div className="space-y-1.5">
            <Label>
              Display Name <span className="text-destructive">*</span>
            </Label>
            <Input
              className={fieldTone(personaData?.display_name?.trim() ? 'ok' : 'idle')}
              placeholder="e.g. Smeeksha"
              value={personaData?.display_name || ''}
              onChange={(e) => patchData({ display_name: e.target.value })}
              onBlur={(e) => requireOnBlur('persona-display-name', e.target.value, 'Display Name')}
            />
          </div>

          {/* 4. Role Description */}
          <div className="space-y-1.5">
            <Label>
              Role Description <span className="text-destructive">*</span>
            </Label>
            <Textarea
              rows={6}
              className={fieldTone(personaData?.role_description?.trim() ? 'ok' : 'idle')}
              placeholder="e.g. You are the voice of the hotel's front desk. You handle room enquiries, bookings..."
              value={personaData?.role_description || ''}
              onChange={(e) => patchData({ role_description: e.target.value })}
              onBlur={(e) => requireOnBlur('persona-role-description', e.target.value, 'Role Description')}
              maxLength={getAiAgentDescriptionCharLimit()}
            />
            <p className="text-right text-xs text-muted-foreground">
              {(personaData?.role_description || '').length}/{getAiAgentDescriptionCharLimit()}
            </p>
          </div>
        </div>
      </div>
    </CollapsibleSection>
  );
}
